// src/utils/i18n.ts

// Interface pour la structure des traductions
interface Translations {
  [lang: string]: {
    [key: string]: string | { [subKey: string]: string }; // Permet des sous-objets comme pour "pause"
  };
}

// Variable pour stocker les traductions chargées
let loadedTranslations: Translations = {};
let translationsLoaded = false;
let loadPromise: Promise<void> | null = null;

const DEFAULT_LANGUAGE = 'fr';
const SUPPORTED_LANGUAGES = ['fr', 'en', 'es']; // Définir explicitement ou déduire après chargement
const STORAGE_KEY = 'genesisTrail_language';
const TRANSLATIONS_PATH = './locales/translations.json'; // Chemin relatif au fichier HTML

let currentLanguage = DEFAULT_LANGUAGE;

/**
 * Charge les traductions depuis le fichier JSON.
 */
async function loadTranslations(): Promise<void> {
  if (translationsLoaded) return Promise.resolve();
  if (loadPromise) return loadPromise; // Retourne la promesse existante si déjà en cours

  loadPromise = (async () => {
      try {
        const response = await fetch(TRANSLATIONS_PATH);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        loadedTranslations = await response.json();
        translationsLoaded = true;
        // console.log("[i18n] Traductions chargées depuis:", TRANSLATIONS_PATH);
      } catch (error) {
        // console.error("[i18n] Erreur lors du chargement des traductions:", error);
        translationsLoaded = false; // Marquer comme non chargé en cas d'erreur
        // Optionnel: utiliser un objet vide ou des traductions par défaut minimales
        loadedTranslations = {};
        throw error; // Renvoyer l'erreur pour que l'appelant puisse la gérer
      } finally {
           loadPromise = null; // Réinitialiser la promesse une fois terminée
      }
  })();
  return loadPromise;
}

/**
 * Assure que les traductions sont chargées avant d'exécuter une action.
 */
async function ensureTranslationsLoaded(): Promise<void> {
    if (!translationsLoaded) {
        await loadTranslations();
    }
}

/**
 * Définit la langue actuelle de l'application.
 * @param lang Code de langue (ex: 'fr', 'en')
 */
export async function setLanguage(lang: string): Promise<void> {
  await ensureTranslationsLoaded();
  const validLang = loadedTranslations[lang] ? lang : 'en'; // Fallback to 'en' if lang is invalid
  currentLanguage = validLang;
  console.log(`[i18n] Language set to: ${currentLanguage}`);
  
  // <<< AJOUT: Sauvegarder dans localStorage >>>
  try {
      localStorage.setItem('genesisTrail_language', currentLanguage);
      console.log(`[i18n] Language preference '${currentLanguage}' saved to localStorage.`);
  } catch (e) {
      console.error("[i18n] Failed to save language to localStorage:", e);
  }
  // <<< FIN AJOUT >>>

  updateUIText(); 
}

/**
 * Obtient la langue actuellement définie.
 * @returns Le code de langue actuel.
 */
export function getCurrentLanguage(): string {
  return currentLanguage;
}

/**
 * Détecte la langue préférée (localStorage, navigateur) et la définit.
 * @returns Le code de langue détecté et défini.
 */
export async function detectLanguage(): Promise<string> {
  // Charger les traductions d'abord pour savoir quelles langues sont supportées
  await ensureTranslationsLoaded();
  const availableLangs = Object.keys(loadedTranslations);

  let detectedLang = DEFAULT_LANGUAGE;
  try {
    const savedLang = localStorage.getItem(STORAGE_KEY);
    if (savedLang && availableLangs.includes(savedLang)) {
      detectedLang = savedLang;
      // console.log(`[i18n] Langue trouvée dans localStorage: ${detectedLang}`);
    } else {
      const browserLang = navigator.language.split('-')[0];
      if (availableLangs.includes(browserLang)) {
        detectedLang = browserLang;
        // console.log(`[i18n] Langue détectée du navigateur: ${detectedLang}`);
      } else {
          // console.log(`[i18n] Langue navigateur (${browserLang}) non supportée ou non trouvée dans JSON, utilisation du défaut: ${DEFAULT_LANGUAGE}`);
          // S'assurer que la langue par défaut est bien disponible
          if (!availableLangs.includes(DEFAULT_LANGUAGE)) {
              detectedLang = availableLangs[0] || 'en'; // Fallback à la première langue dispo ou anglais
              // console.warn(`[i18n] Langue par défaut (${DEFAULT_LANGUAGE}) non trouvée, utilisation de: ${detectedLang}`);
          }
      }
    }
  } catch (e) {
     // console.warn("[i18n] Impossible d'accéder à localStorage ou navigator.language:", e);
  }

  // Si la langue détectée n'est pas dans le fichier JSON, revenir au défaut ou à la première dispo
  if (!availableLangs.includes(detectedLang)) {
      // console.warn(`[i18n] Langue détectée (${detectedLang}) invalide après vérifications, retour au défaut (${DEFAULT_LANGUAGE}) ou à la première langue disponible.`);
      detectedLang = availableLangs.includes(DEFAULT_LANGUAGE) ? DEFAULT_LANGUAGE : (availableLangs[0] || 'en');
  }

  await setLanguage(detectedLang); // Appelle la version async de setLanguage
  return detectedLang;
}

/**
 * Traduit une clé donnée dans la langue actuelle.
 * NOTE: Cette fonction reste synchrone mais dépend du chargement préalable des traductions.
 * @param key La clé de traduction (ex: 'menu.title' ou 'pause.title').
 * @param replacements Un objet pour remplacer les placeholders (ex: { count: 5 }).
 * @param lang Code de langue optionnel pour forcer une langue spécifique.
 * @returns La chaîne traduite ou la clé si non trouvée.
 */
export function translate(key: string, replacements: { [k: string]: string | number } = {}, lang: string = currentLanguage): string {
  if (!translationsLoaded) {
    // console.warn(`[i18n] Tentative de traduction ('${key}') avant le chargement complet des traductions. Retour de la clé.`);
    return key;
  }

  const langTranslations = loadedTranslations[lang] || loadedTranslations[DEFAULT_LANGUAGE];
  if (!langTranslations) {
      // console.warn(`[translate] Traduction échouée pour '${key}': langTranslations non trouvé pour lang '${lang}'. loadedTranslations:`, Object.keys(loadedTranslations));
      return key;
  }

  let translation: any = key; // Default to key

  // 1. Essayer de trouver la clé complète directement
  const directTranslation = langTranslations[key];
  if (directTranslation !== undefined) {
      translation = directTranslation;
      // Si la valeur trouvée est un objet (comme pour "pause"), mais qu'on voulait la clé de base,
      // on retourne la clé pour signaler une erreur d'utilisation.
      // On ne devrait pas demander "pause" mais "pause.title".
      if (typeof translation === 'object') {
          // console.warn(`[translate] La clé '${key}' correspond à un objet, pas une chaîne. Avez-vous oublié une sous-clé (ex: ${key}.title) ?`);
          translation = key; // Retourne la clé pour indiquer l'erreur
      }
  }
  // 2. Si non trouvée directement ET qu'elle contient un point, essayer comme clé imbriquée
  else if (key.includes('.')) {
      const keys = key.split('.');
      let currentLevel: any = langTranslations;
      let found = true;
      for (const k of keys) {
          if (currentLevel && typeof currentLevel === 'object' && k in currentLevel) {
              currentLevel = currentLevel[k];
          } else {
              found = false;
              break;
          }
      }
      // Si on a trouvé une valeur à la fin du chemin
      if (found) {
          translation = currentLevel;
      }
      // Si on a trouvé un objet au lieu d'une chaîne à la fin, c'est une erreur.
      if (typeof translation === 'object') {
           // console.warn(`[translate] La clé imbriquée '${key}' a abouti à un objet, pas une chaîne.`);
           translation = key;
      }
  }

  // 3. Vérifier si on a bien une chaîne à la fin (après essai direct ou imbriqué)
  if (typeof translation !== 'string') {
      // Si ce n'est pas une string à ce stade, c'est que la clé n'a pas été trouvée
      // (le log d'avertissement spécifique sur le type est moins utile ici, on le commente)
      // console.warn(`[translate] La clé '${key}' n\'a pas retourné une chaîne ('${lang}'). Type: ${typeof translation}`);
      translation = key; // Assurer qu'on retourne la clé si échec
  }

  // Remplacer les placeholders
  Object.keys(replacements).forEach(placeholder => {
    const regex = new RegExp(`\{${placeholder}\}`, 'g');
    translation = (translation as string).replace(regex, String(replacements[placeholder]));
  });

  return translation as string;
}

/**
 * Met à jour le contenu texte des éléments HTML de manière asynchrone.
 * @param container Élément DOM optionnel à scanner. Par défaut, document.body.
 */
export async function updateUIText(container: HTMLElement | Document = document.body): Promise<void> {
    // console.log(`[updateUIText] Début. Translations loaded: ${translationsLoaded}`);
    await ensureTranslationsLoaded(); // S'assurer que les traductions sont là
    // console.log(`[updateUIText] ensureTranslationsLoaded passé. Translations loaded: ${translationsLoaded}`);
    if (!container) {
        // console.warn("[updateUIText] Conteneur non valide.");
        return;
    }
    if (!translationsLoaded) {
        // console.warn("[updateUIText] Appelée mais traductions non chargées !");
        return;
    }

    const elements = container.querySelectorAll<HTMLElement>('[data-translate-key]');
    // console.log(`[updateUIText] ${elements.length} éléments trouvés avec [data-translate-key]`);

    elements.forEach(element => {
        const key = element.dataset.translateKey;
        if (key) {
            const translatedText = translate(key);
            // console.log(`[updateUIText] Traitement clé: ${key} -> Traduction: ${translatedText} pour element:`, element.outerHTML.substring(0, 100));

            // Traduction des attributs (synchrone car translate est synchrone après chargement)
            const attrsToTranslate = element.dataset.translateAttrs?.split(',') || [];
            attrsToTranslate.forEach(attr => {
                 const trimmedAttr = attr.trim();
                 if (trimmedAttr && element.hasAttribute(trimmedAttr)) {
                     const attrKey = element.dataset[`translateAttr${trimmedAttr.charAt(0).toUpperCase() + trimmedAttr.slice(1)}Key`] || `${key}.${trimmedAttr}`;
                     const translatedAttr = translate(attrKey);
                     // console.log(`[updateUIText] -- Attribut ${trimmedAttr}: ${attrKey} -> ${translatedAttr}`);
                     element.setAttribute(trimmedAttr, translatedAttr);
                 }
            });

            // Traduction du contenu texte (version simplifiée)
            if (element.tagName !== 'SELECT') {
                // Vérifier si le contenu actuel est différent de la traduction voulue
                if (element.textContent !== translatedText) {
                    // console.log(`[updateUIText] -- MISE À JOUR textContent de '${element.textContent}' vers '${translatedText}'`);
                    element.textContent = translatedText;
                } else {
                     // console.log(`[updateUIText] -- textContent déjà à jour ('${element.textContent}').`);
                }
            }
        } else {
            // console.warn("[updateUIText] Element trouvé sans data-translateKey valide?", element);
        }
    });

    // Mise à jour des titres de boutons
     const layoutButton = container.querySelector<HTMLButtonElement>('#layoutToggleButton');
     if (layoutButton) {
         const currentLayoutMode = document.body.classList.contains('layout-landscape') ? 'portrait' : 'landscape';
         layoutButton.title = translate(`layout.toggle.${currentLayoutMode}`);
     }
     const soundButton = container.querySelector<HTMLButtonElement>('#sound-toggle-button');
     if (soundButton) {
          const isMuted = soundButton.classList.contains('muted');
          soundButton.title = translate(isMuted ? 'sound.toggle.on' : 'sound.toggle.off');
     }
}

// Plus d'initialisation automatique ici, elle se fera via l'appel explicite
// de detectLanguage() ou setLanguage() depuis l'extérieur (ex: menu.js ou main.ts) 